import { useFilterSpices } from "@/hooks/useFilter";
import { useCache } from "@/hooks/useCache";
import { Loading } from "@/components/ui/Loading";
import { Filter } from "./Filter";
import type { FilterOptions, SortOptions } from "./Filter";
import { SpiceItem } from "./Item";
import { useState, useRef, useCallback } from "react";

export function List({
  selectedSpice,
  setSelectedSpice,
  className,
}: {
  selectedSpice: number | null;
  setSelectedSpice: (s: number | null) => void;
  className?: string;
}) {
  const { data, isLoading } = useCache();
  const [spices] = useFilterSpices();
  const [search, setSearch] = useState("");
  const [filterBy, setFilterBy] = useState<FilterOptions>("all");
  const [sortBy, setSortBy] = useState<SortOptions>("name");

  const containerRef = useRef<HTMLDivElement>(null);
  const scrollToTop = useCallback(() => containerRef.current?.scrollTo({ top: 0, behavior: "smooth" }), []);

  const allItems = data ? Object.values(data) : [];

  let items = allItems.filter((item) => {
    const matchesSearch = item.name.toLowerCase().includes(search.toLowerCase());
    const isAdded = spices.has(item.id);
    if (filterBy === "added") return matchesSearch && isAdded;
    if (filterBy === "not-added") return matchesSearch && !isAdded;
    return matchesSearch;
  });

  if (sortBy === "name") {
    items = [...items].sort((a, b) => a.name.localeCompare(b.name));
  } else if (sortBy === "quantity") {
    items = [...items].sort((a, b) => {
      const qtyA = spices.get(a.id)?.quantity ?? 0;
      const qtyB = spices.get(b.id)?.quantity ?? 0;
      return qtyB - qtyA;
    });
  }

  return (
    <div className={`${className ?? "flex-1 min-h-0"} flex flex-col`}>
      <Filter
        value={search}
        onChange={setSearch}
        filterBy={filterBy}
        onFilterChange={setFilterBy}
        sortBy={sortBy}
        onSortChange={setSortBy}
        onScrollTop={scrollToTop}
      />

      {isLoading ? (
        <Loading message="loading spices..." className="p-4" />
      ) : (
        <div ref={containerRef} className="overflow-y-auto flex-1 min-h-0 @container">
          <div className="p-4 rounded-lg">
            <div className="grid grid-cols-1 lg:@[280px]:grid-cols-2 gap-6">
              {items.map((item) => {
                const spiceData = spices.get(item.id);
                const hasQuantity = !!(spiceData && spiceData.quantity > 0);
                const displayQuantity = spiceData ? Math.round(spiceData.quantity) : 0;
                return (
                  <SpiceItem
                    key={item.id}
                    item={item}
                    isSelected={selectedSpice === item.id}
                    displayQuantity={displayQuantity}
                    hasQuantity={hasQuantity}
                    onSelect={setSelectedSpice}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
